import type { Question, SpecialQuestion } from '../data/questions';
import { questions, specialQuestions } from '../data/questions';
import { TYPE_LIBRARY, NORMAL_TYPES } from '../data/types';
import { dimensionMeta, dimensionOrder, DRUNK_TRIGGER_QUESTION_ID } from '../data/dimensions';

export interface RankedType {
  code: string;
  cn: string;
  intro: string;
  desc: string;
  pattern: string;
  distance: number;
  exact: number;
  similarity: number;
}

export interface TestResult {
  rawScores: Record<string, number>;
  levels: Record<string, string>;
  ranked: RankedType[];
  bestNormal: RankedType;
  finalType: { code: string; cn: string; intro: string; desc: string };
  modeKicker: string;
  badge: string;
  sub: string;
  special: boolean;
  secondaryType: RankedType | null;
}

export function shuffle<T>(array: T[]): T[] {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
  }
  return arr;
}

function sumToLevel(score: number): string {
  if (score <= 3) return 'L';
  if (score === 4) return 'M';
  return 'H';
}

function levelNum(level: string): number {
  return { L: 1, M: 2, H: 3 }[level] ?? 2;
}

function parsePattern(pattern: string): number[] {
  return pattern.replace(/-/g, '').split('').map(levelNum);
}

function isDrunkTriggered(answers: Record<string, number>): boolean {
  return answers[DRUNK_TRIGGER_QUESTION_ID] === 2;
}

export function buildQuestionList(): Question[] {
  const shuffled = shuffle(questions);
  const insertIndex = Math.floor(Math.random() * shuffled.length) + 1;
  const gate = specialQuestions[0];
  if (!gate) return shuffled;
  return [
    ...shuffled.slice(0, insertIndex),
    gate,
    ...shuffled.slice(insertIndex),
  ];
}

export function getVisibleQuestions(
  baseQuestions: Question[],
  answers: Record<string, number>,
): (Question | SpecialQuestion)[] {
  const visible: (Question | SpecialQuestion)[] = [...baseQuestions];
  const gateIndex = visible.findIndex(q => q.id === 'drink_gate_q1');
  if (gateIndex !== -1 && answers['drink_gate_q1'] === 3) {
    const trigger = specialQuestions[1];
    if (trigger) visible.splice(gateIndex + 1, 0, trigger);
  }
  return visible;
}

export function computeResult(answers: Record<string, number>): TestResult {
  const rawScores: Record<string, number> = {};
  const levels: Record<string, string> = {};

  for (const dim of Object.keys(dimensionMeta)) { rawScores[dim] = 0; }
  for (const q of questions) {
    rawScores[q.dim]! += Number(answers[q.id] ?? 0);
  }
  for (const [dim, score] of Object.entries(rawScores)) {
    levels[dim] = sumToLevel(score);
  }

  const userVector = dimensionOrder.map(dim => levelNum(levels[dim]!));

  const ranked = NORMAL_TYPES.map(type => {
    const vector = parsePattern(type.pattern);
    let distance = 0;
    let exact = 0;
    for (let i = 0; i < vector.length; i++) {
      const diff = Math.abs(userVector[i]! - vector[i]!);
      distance += diff;
      if (diff === 0) exact += 1;
    }
    const similarity = Math.max(0, Math.round((1 - distance / 30) * 100));
    const libEntry = TYPE_LIBRARY[type.code]!;
    return {
      ...type,
      ...libEntry,
      distance,
      exact,
      similarity,
    };
  }).sort((a, b) => {
    if (a.distance !== b.distance) return a.distance - b.distance;
    if (b.exact !== a.exact) return b.exact - a.exact;
    return b.similarity - a.similarity;
  });

  const bestNormal = ranked[0]!;
  const drunkTriggered = isDrunkTriggered(answers);

  let finalType;
  let modeKicker = '你的主类型';
  let badge = `匹配度 ${bestNormal.similarity}% · 精准命中 ${bestNormal.exact}/15 维`;
  let sub = '维度命中度较高，当前结果可视为你的第一人格画像';
  let special = false;
  let secondaryType: RankedType | null = null;

  if (drunkTriggered) {
    finalType = TYPE_LIBRARY.DRUNK!;
    secondaryType = bestNormal;
    modeKicker = '隐藏人格已激活';
    badge = '匹配度 100% · 酒精异常因子已接管';
    sub = '乙醇亲和性过强，系统已直接跳过常规人格审判。';
    special = true;
  } else if (bestNormal.similarity < 60) {
    finalType = TYPE_LIBRARY.HHHH!;
    modeKicker = '系统强制兜底';
    badge = `标准人格库最高匹配仅 ${bestNormal.similarity}%`;
    sub = '标准人格库对你的脑回路集体罢工了，于是系统把你强制分配给了 HHHH。';
    special = true;
  } else {
    finalType = bestNormal;
  }

  return {
    rawScores,
    levels,
    ranked,
    bestNormal,
    finalType,
    modeKicker,
    badge,
    sub,
    special,
    secondaryType,
  };
}
